import { useMemo, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { useFindRelated } from "../../hooks/use-find-related";
import { useBackRefs, type BackRef } from "../../hooks/use-back-refs";
import { BackReferences } from "../entity/BackReferences";
import type {
  FindRelatedResult,
  MentionEntry,
  MentionedInEntry,
  RowBackReferences,
} from "../../../lib/types";

/**
 * Sprint 26.2 — the cross-tag "Related" surface.
 *
 * ONE component, mounted on a task, an agent, and an entity row. It answers
 * "what across the whole workspace — across agents — connects to this thing?"
 * by composing the existing read-tools:
 *
 *   • `find_related` — the four-layer mention graph (explicit entity refs, tree
 *     position, reverse + forward mentions). Lit up for entities and library
 *     nodes (agents); a task id returns nothing and that's fine.
 *   • `get_back_references` — inbound refs the mention graph doesn't carry
 *     (activities *regarding* a task/routine; content that mentions a node).
 *   • `seedBackRefs` — for an entity row the caller already holds the rich
 *     grouped back-refs from `get_row_detail`; we render them with the existing
 *     {@link BackReferences} grouping (so the FK drill-in panel UX is preserved)
 *     and layer the find_related graph beneath.
 *
 * Every row links out and shows its kind so cross-agent connections are legible.
 */

export type RelatedSubjectKind = "task" | "node" | "entity";

export interface RelatedSectionProps {
  subjectKind: RelatedSubjectKind;
  subjectId: string;
  /** Entity row only — the grouped back-refs already loaded by get_row_detail. */
  seedBackRefs?: RowBackReferences;
  /** Entity row only — opens a referenced record's slide-in panel (FK drill-in). */
  onOpenRef?: (entityTypeSlug: string, entityId: string) => void;
  title?: string;
  /** Page mount renders the section heading; pane/overview supply their own. */
  heading?: boolean;
}

interface RelatedItem {
  key: string;
  icon: string;
  name: string;
  /** Short "kind · context" line under the name. */
  kind: string;
  context?: string;
  /** Where clicking the row navigates; absent rows render but don't link. */
  href?: string;
}

// --- href + icon helpers -----------------------------------------------------

function nodeHref(id: string, nodeType?: string | null): string {
  return nodeType === "agent" ? `/agents/${id}` : `/library/${id}`;
}

function nodeIcon(nodeType?: string | null): string {
  switch (nodeType) {
    case "agent": return "ti-robot";
    case "page": return "ti-file-text";
    case "memory": return "ti-message-circle";
    case "folder": return "ti-folder";
    default: return "ti-link";
  }
}

function backRefHref(ref: BackRef): string | undefined {
  switch (ref.kind) {
    case "agent": return `/agents/${ref.id}`;
    case "page":
    case "memory":
    case "node": return `/library/${ref.id}`;
    case "task": return `/tasks/${ref.id}`;
    case "activity": return "/activity";
    default: return undefined;
  }
}

function backRefIcon(ref: BackRef): string {
  switch (ref.kind) {
    case "agent": return "ti-robot";
    case "page": return "ti-file-text";
    case "memory": return "ti-message-circle";
    case "task": return "ti-checkbox";
    case "activity": return "ti-history";
    default: return "ti-link";
  }
}

// --- normalisers -------------------------------------------------------------

/** A find_related entry may be redacted to an opaque restricted placeholder. */
function isRestricted(v: unknown): boolean {
  return !!v && typeof v === "object" && (v as { kind?: string }).kind === "restricted";
}

interface ExplicitRef {
  entity_id: string;
  entity_type: string;
  field: string;
  name: string;
  direction: "inbound" | "outbound";
}

interface TreeContext {
  node_id: string;
  node_name: string;
  node_type: string;
  path: string;
}

function fromFindRelated(result: FindRelatedResult): RelatedItem[] {
  const rels = result.relationships ?? {};
  const items: RelatedItem[] = [];

  for (const raw of (rels.explicit_references ?? []) as unknown[]) {
    if (isRestricted(raw)) continue;
    const r = raw as ExplicitRef;
    items.push({
      key: `expl:${r.entity_id}:${r.field}:${r.direction}`,
      icon: "ti-database",
      name: r.name || r.entity_id,
      kind: r.entity_type,
      context: r.direction === "outbound" ? `references via ${r.field}` : `referenced by ${r.field}`,
      href: `/entities/${r.entity_type}/${r.entity_id}`,
    });
  }

  for (const raw of (rels.tree_context ?? []) as unknown[]) {
    if (isRestricted(raw)) continue;
    const t = raw as TreeContext;
    // The subject's own tree position isn't a "related" item — skip self.
    if (t.node_id === result.id) continue;
    items.push({
      key: `tree:${t.node_id}`,
      icon: nodeIcon(t.node_type),
      name: t.node_name,
      kind: t.node_type,
      context: t.path && t.path !== "(root)" ? t.path : undefined,
      href: nodeHref(t.node_id, t.node_type),
    });
  }

  for (const m of (rels.mentioned_in ?? []) as MentionedInEntry[]) {
    if (isRestricted(m)) continue;
    const isEntity = m.source_type === "entity";
    items.push({
      key: `in:${m.source_id}`,
      icon: isEntity ? "ti-database" : nodeIcon(m.source_node_type),
      name: m.source_name || m.source_id,
      kind: isEntity ? (m.source_slug ?? "record") : (m.source_node_type ?? "page"),
      context: m.snippet ? `mentions this — "${m.snippet}"` : "mentions this",
      href: isEntity && m.source_slug
        ? `/entities/${m.source_slug}/${m.source_id}`
        : nodeHref(m.source_id, m.source_node_type),
    });
  }

  for (const m of (rels.mentions ?? []) as MentionEntry[]) {
    if (isRestricted(m)) continue;
    items.push({
      key: `out:${m.target_id}`,
      icon: "ti-database",
      name: m.target_name || m.target_id,
      kind: m.target_slug,
      context: "mentioned here",
      href: `/entities/${m.target_slug}/${m.target_id}`,
    });
  }

  return items;
}

function fromBackRefs(refs: BackRef[]): RelatedItem[] {
  return refs.map((r) => ({
    key: `back:${r.id}`,
    icon: backRefIcon(r),
    name: r.name || r.id,
    kind: r.kind,
    context: r.location === "regarding" ? "logged about this" : undefined,
    href: backRefHref(r),
  }));
}

// --- component ---------------------------------------------------------------

export function RelatedSection({
  subjectKind,
  subjectId,
  seedBackRefs,
  onOpenRef,
  title = "Related",
  heading = false,
}: RelatedSectionProps) {
  const navigate = useNavigate();
  const findRelated = useFindRelated(subjectId);
  // The entity row's regarding back-refs already arrive via get_row_detail
  // (seedBackRefs) — don't double-fetch them. Task / node lean on get_back_references.
  const backRefs = useBackRefs(subjectKind, subjectId, !seedBackRefs);

  const items = useMemo(() => {
    const out: RelatedItem[] = [];
    if (findRelated.data) out.push(...fromFindRelated(findRelated.data));
    if (!seedBackRefs && backRefs.data) out.push(...fromBackRefs(backRefs.data.refs));
    // De-dupe by id (a thing can be both mentioned and a back-ref).
    const seen = new Set<string>();
    return out.filter((it) => {
      const id = it.href ?? it.key;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [findRelated.data, backRefs.data, seedBackRefs]);

  const loading = findRelated.isLoading || (!seedBackRefs && backRefs.isLoading);
  const hasSeed = !!seedBackRefs;
  const isEmpty = !loading && items.length === 0 && !hasSeed;

  return (
    <section style={{ marginBottom: 16 }} data-testid="related-section">
      {title && (heading ? <div style={headingRow}>{title}</div> : <div style={label}>{title}</div>)}

      {/* Entity row: the grouped regarding back-refs (existing UX, FK drill-in). */}
      {seedBackRefs && <BackReferences backRefs={seedBackRefs} onOpenRef={onOpenRef} />}

      {loading ? (
        <div style={hint}>Looking across the workspace…</div>
      ) : items.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: hasSeed ? 10 : 0 }}>
          {items.map((it) => (
            <RelatedRow key={it.key} item={it} onOpen={() => it.href && navigate(it.href)} />
          ))}
        </div>
      ) : isEmpty ? (
        <div style={empty}>Nothing connected yet. Links form as agents mention this across the workspace.</div>
      ) : null}
    </section>
  );
}

function RelatedRow({ item, onOpen }: { item: RelatedItem; onOpen: () => void }) {
  const clickable = !!item.href;
  return (
    <div
      style={{ ...row, cursor: clickable ? "pointer" : "default" }}
      onClick={clickable ? onOpen : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } } : undefined}
    >
      <i className={`ti ${item.icon}`} style={{ fontSize: 15, color: "var(--color-text-tertiary)", flexShrink: 0, alignSelf: "flex-start", marginTop: 1 }} aria-hidden="true" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={nameStyle}>{item.name}</span>
        <span style={metaStyle}>
          <span style={kindPill}>{item.kind}</span>
          {item.context && <span style={contextStyle}>{item.context}</span>}
        </span>
      </div>
      {clickable && (
        <i className="ti ti-chevron-right" style={{ fontSize: 13, color: "var(--color-text-tertiary)", flexShrink: 0, alignSelf: "center" }} aria-hidden="true" />
      )}
    </div>
  );
}

const headingRow: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  marginBottom: 8,
};

const label: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  marginBottom: 6,
};

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  padding: "9px 11px",
};

const nameStyle: CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const metaStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginTop: 2,
  minWidth: 0,
};

const kindPill: CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-tertiary)",
  flexShrink: 0,
};

const contextStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-secondary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const hint: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
};

const empty: CSSProperties = {
  fontSize: 13,
  fontStyle: "italic",
  color: "var(--color-text-tertiary)",
};
